/**
 * 主应用组件
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react'; // 添加 useCallback, useMemo, useEffect
import { useNavigate, useParams } from 'react-router-dom'; // 新增：路由支持
import { useAppState } from './hooks/useAppState';
import { useAuth } from './hooks/useAuth'; // 新增：认证 hook
import { projectApi, quotationApi, companyApi } from './services/api'; // 新增
import { draftStorage } from './utils/draftStorage'; // 新增：用于检查章节完成状态
import Sidebar from './components/Sidebar';
import StepSidebar from './components/StepSidebar';
import FloatingStepButtons from './components/FloatingStepButtons';
import Toast, { ToastType } from './components/Toast';
import ConfirmDialog from './components/ConfirmDialog';
import LoadingOverlay from './components/LoadingOverlay';
import LoginDialog from './components/LoginDialog'; // 新增：登录弹框
import ProjectSetup from './pages/ProjectSetup';
import QuickStart from './pages/QuickStart'; // 新增：快速开始
import DocumentAnalysis from './pages/DocumentAnalysis';
import QuotationEdit from './pages/QuotationEdit'; // 新增：分项报价
import OutlineEdit from './pages/OutlineEdit';
import ContentEdit from './pages/ContentEdit';
import ProjectManagement from './pages/ProjectManagement';
import TemplateManagement from './pages/TemplateManagement';
import CompanyManagement from './pages/CompanyManagement';
import CompanyEdit from './pages/CompanyEdit'; // 新增：主体编辑
import ResourceManagement from './pages/ResourceManagement';
import TokenManagement from './pages/TokenManagement'; // 新增：Token管理
import UserManagement from './pages/UserManagement'; // 新增：人员管理
import ProjectGroupManagement from './pages/ProjectGroupManagement'; // 新增：项目组管理
import TokenProgressBar from './components/TokenProgressBar'; // 新增：Token进度条
import TokenAlertDialog from './components/TokenAlertDialog'; // 新增：Token预警弹窗
import { useTokenCheck } from './hooks/useTokenCheck'; // 新增：Token检查hook
import { UserInfo, QuotationData } from './types';

interface AppProps {
  /** 初始视图，用于根据路由决定展示哪个模块 */
  initialView?: 'project-management' | 'new-project' | 'template-management' | 'company-management' | 'company-edit' | 'resource-management' | 'token-management' | 'user-management' | 'project-group-management';
}

function App({ initialView }: AppProps) {
  const navigate = useNavigate(); // 路由导航
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>(); // URL参数

  // 认证状态
  const auth = useAuth();

  // Token 余额检查和预警（只在标书生成步骤和上传营业执照时显示欠费拦截）
  const isNewProjectPage = initialView === 'new-project';
  const { alert, balance, checkTokenBalance, handleRecharge, closeAlert, handleContinue } = useTokenCheck({ 
    enableOverdraftBlock: isNewProjectPage,
    onContinue: () => {
      // 用户点击"继续生成"，触发全局事件让各调用方带上 force 参数重试
      window.dispatchEvent(new CustomEvent('token:continue'));
    }
  });
  
  const {
    state,
    updateProjectConfig,
    updateStep,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
    updateQuotation, // 新增
    updateTemplates, // 新增：更新招标模版
    updateQuotationData, // 新增：更新分项报价数据
    updateSelectedChapter,
    markAnalysisCompleted,
    markQuotationCompleted,
    markOutlineCompleted,
    nextStep,
    prevStep,
    resetSubsequentSteps,
    updateStepAndResetMax,
    loadProject,
    resetProject,
    setProjectId,
  } = useAppState();

  const steps = ['项目配置', '招标解析', '分项报价', '目录生成', '文件生成'];

  // 当前视图状态：根据 URL 和路由初始视图决定
  const [currentView, setCurrentView] = useState<string>(() => {
    // 如果 URL 中有 projectId，默认显示编辑视图
    if (urlProjectId) return 'new-project';
    // 否则根据传入的初始视图决定
    return initialView || 'project-management';
  });

  // 当 initialView 变化时（路由切换），更新 currentView
  useEffect(() => {
    if (initialView && initialView !== currentView) {
      setCurrentView(initialView);
    }
  }, [initialView]);

  // 全局监听登录成功事件，刷新页面数据
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log('[App] 登录成功，刷新页面数据');
      // 触发页面刷新事件，让各个组件重新加载数据
      window.dispatchEvent(new CustomEvent('app:refresh-data'));
    };

    window.addEventListener('auth:login-success', handleLoginSuccess);
    return () => window.removeEventListener('auth:login-success', handleLoginSuccess);
  }, []);

  // 步骤导航收起状态
  const [isStepSidebarCollapsed, setIsStepSidebarCollapsed] = useState(false);

  // 从 URL 加载项目
  useEffect(() => {
    if (urlProjectId) {
      // URL 中有项目 ID，加载项目
      if (!state.projectId || state.projectId !== urlProjectId) {
        handleEditProject(urlProjectId);
      }
    } else {
      // 没有 URL 参数，清空项目状态
      if (state.projectId) {
        resetProject();
      }
    }
  }, [urlProjectId]);
  
  // 注意：不再使用 useEffect 自动同步 current_step
  // 原因：handleFloatingNext/handlePrevStep 等函数已经显式调用 updateProgress
  // 如果再用 useEffect 监听 state.currentStep，会导致重复调用和 flushSync 警告

  // Toast 通知状态
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: [],
  });

  // 全屏加载状态
  const [loading, setLoading] = useState<{
    show: boolean;
    message: string;
  }>({
    show: false,
    message: '正在加载项目数据...',
  });

  // 确认对话框状态
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 主体检查确认弹框状态
  const [companyConfirmDialog, setCompanyConfirmDialog] = useState<{
    show: boolean;
    hasCompany: boolean | null;
  }>({
    show: false,
    hasCompany: null,
  });
  
  // 防抖保存目录到数据库（500ms）
  const debouncedSaveOutline = useMemo(
    () => {
      let timeoutId: NodeJS.Timeout;
      return (projectId: string, outlineData: any) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            await projectApi.saveOutline(projectId, outlineData);
          } catch (error) {
            console.error('保存目录失败:', error);
          }
        }, 500);
      };
    },
    []
  );
  
  // 立即保存目录到数据库（AI生成完毕时使用）
  const saveOutlineImmediately = useCallback(async (projectId: string, outlineData: any) => {
    try {
      await projectApi.saveOutline(projectId, outlineData);
    } catch (error) {
      console.error('保存目录失败:', error);
    }
  }, []);
  
  // 刷新目录数据（用于字数修改等场景）
  const refreshOutlineData = useCallback(async (projectId: string) => {
    try {
      const response = await projectApi.getProjectOutlines(projectId);
      if (response.data.outline_items) {
        // 保留原有的outlineData结构，只更新outline数组
        const updatedOutlineData = {
          outline: response.data.outline_items,
          project_name: state.outlineData?.project_name || '',
          project_overview: state.outlineData?.project_overview || ''
        };
        updateOutline(updatedOutlineData);
      }
    } catch (error) {
      console.error('刷新目录失败:', error);
    }
  }, [updateOutline, state.outlineData]);
  
  // 防抖保存章节到数据库（3秒）
  const debouncedSaveChapter = useMemo(
    () => {
      const timeouts = new Map<string, NodeJS.Timeout>();
      return (projectId: string, chapterId: string, content: string) => {
        // 清除该章节的旧定时器
        const oldTimeout = timeouts.get(chapterId);
        if (oldTimeout) {
          clearTimeout(oldTimeout);
        }
        
        // 设置新定时器
        const newTimeout = setTimeout(async () => {
          try {
            await projectApi.saveChapter({
              project_id: projectId,
              outline_item_id: chapterId,
              content: content,
            });
            timeouts.delete(chapterId);
          } catch (error) {
            console.error(`保存章节 ${chapterId} 失败:`, error);
          }
        }, 3000); // 3秒防抖
        
        timeouts.set(chapterId, newTimeout);
      };
    },
    []
  );

  // 显示 Toast
  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  // 关闭 Toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // 显示确认对话框
  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      onConfirm,
    });
  };

  // 关闭确认对话框
  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, show: false }));
  };

  // 退出登录
  const handleLogout = () => {
    // 清除认证信息（内部已包含跳转逻辑）
    auth.logout();
  };

  // 检查用户是否有主体，如果没有则显示确认弹框
  const checkCompanyBeforeCreate = async () => {
    try {
      const response = await companyApi.getCompanies();
      const hasCompany = response.data && response.data.length > 0;
      if (!hasCompany) {
        setCompanyConfirmDialog({ show: true, hasCompany: false });
        return false;
      }
      return true;
    } catch (e) {
      setCompanyConfirmDialog({ show: true, hasCompany: false });
      return false;
    }
  };

  // 执行新建标书（已有主体时调用）
  const doCreateProject = () => {
    setCurrentView('new-project');
    resetProject(); // 使用 resetProject 清空缓存
    updateStep(0);
    navigate('/create');
  };

  // 视图切换处理
  const handleViewChange = async (view: string) => {
    if (view === 'new-project') {
      // 新建标书：先检查主体
      const hasCompany = await checkCompanyBeforeCreate();
      if (!hasCompany) return; // 没有主体，显示弹框后返回，不继续跳转
      // 有主体，执行新建标书
      doCreateProject();
      return;
    }

    setCurrentView(view);

    // 管理类视图：只切换路由，不重置项目状态
    if (view === 'project-management') {
      navigate('/projects');
    } else if (view === 'template-management') {
      navigate('/templates');
    } else if (view === 'company-management') {
      navigate('/companies');
    } else if (view === 'resource-management') {
      navigate('/resources');
    }
  };

  // 编辑项目（从项目管理点击）
  const handleEditProject = async (projectId: string, currentStep?: number) => {
    // 如果是新建项目，走新建标书逻辑（包含主体检查）
    if (projectId === 'new') {
      handleViewChange('new-project');
      return;
    }

    try {
      // 显示全屏加载
      setLoading({ show: true, message: '正在加载项目数据...' });
      
      // 如果是不同的项目，清空状态（localStorage 由 loadProject 统一清空）
      if (state.projectId && state.projectId !== projectId) {
        resetProject(); // 清空状态
      }
      
      // 使用通用加载函数
      const projectData = await loadProjectData(projectId);
      
      // 恢复项目状态（loadProject 会清空 localStorage 并加载新数据）
      loadProject(projectData);
      
      // 关闭加载
      setLoading({ show: false, message: '' });
      
      // 切换到编辑视图
      setCurrentView('new-project');
      
      // 更新 URL
      navigate(`/project/${projectId}`, { replace: true });
    } catch (error) {
      console.error('加载项目失败:', error);
      setLoading({ show: false, message: '' });
      showToast('error', '加载项目失败', ['请稍后重试']);
    }
  };

  // 验证项目配置
  const validateProjectConfig = (): boolean => {
    const config = state.projectConfig;
    const errors: string[] = [];

    if (!config.alias?.trim()) {
      errors.push('请填写项目别名');
    }
    if (!config.company) {
      errors.push('请选择投标主体');
    }
    if (!config.projectType) {
      errors.push('请选择项目类型');
    }
    if (!config.aiModelName) {
      errors.push('请选择大模型提供商');
    }
    if (!config.selectedModel) {
      errors.push('请选择具体的模型');
    }
    if (!state.fileContent) {
      errors.push('请上传招标文件');
    }

    if (errors.length > 0) {
      showToast('error', '请完善以下信息', errors);
      return false;
    }
    return true;
  };

  // 通用的项目数据加载函数（根据 max_reached_step 自动加载所需数据）
  const loadProjectData = async (projectId: string) => {
    try {
      // 1. 获取项目基础信息
      const baseResponse = await projectApi.getProject(projectId);
      const projectData = baseResponse.data;
      const maxStep = projectData.progress?.max_reached_step ?? 0;
      
      // 2. 根据 max_reached_step 并行请求需要的数据
      const requests: Promise<any>[] = [];
      
      if (maxStep >= 0) {
        requests.push(
          projectApi.getProjectFiles(projectId).then(res => ({ type: 'files', data: res.data }))
        );
      }
      
      if (maxStep >= 1) {
        requests.push(
          projectApi.getProjectAnalyses(projectId).then(res => ({ type: 'analyses', data: res.data }))
        );
      }
      
      if (maxStep >= 2) {
        requests.push(
          projectApi.getProjectOutlines(projectId).then(res => ({ type: 'outlines', data: res.data }))
        );
        // 获取分项报价数据
        requests.push(
          quotationApi.getLatest(projectId).then(res => ({ type: 'quotation', data: res.data })).catch(() => ({ type: 'quotation', data: null }))
        );
      }
      
      if (maxStep >= 3) {
        requests.push(
          projectApi.getProjectChapters(projectId).then(res => ({ type: 'chapters', data: res.data }))
        );
      }
      
      // 3. 并行请求所有数据
      const responses = await Promise.all(requests);
      
      // 4. 合并数据
      responses.forEach(response => {
        if (response.type === 'files' && response.data.files) {
          projectData.files = response.data.files;
        } else if (response.type === 'analyses' && response.data.analyses) {
          projectData.analyses = response.data.analyses;
        } else if (response.type === 'outlines' && response.data.outline_items) {
          projectData.outline_items = response.data.outline_items;
        } else if (response.type === 'chapters' && response.data.chapters) {
          projectData.chapters = response.data.chapters;
        } else if (response.type === 'quotation' && response.data) {
          // 将分项报价数据保存到 analyses 中，以便 loadProject 函数能够读取
          if (!projectData.analyses) {
            projectData.analyses = [];
          }
          projectData.analyses.push({
            analysis_type: 'quotation_filled',
            content: JSON.stringify({
              items: response.data.items || [],
              maxTotalPrice: response.data.max_total_price,
              warningPercent: response.data.warning_percent || 80,
              notes: response.data.notes || ''
            })
          });
        }
      });
      
      return projectData;
      
    } catch (error) {
      console.error('❌ 加载项目数据失败:', error);
      throw error;
    }
  };

  // 处理步骤切换（左侧步骤面板点击）
  const handleStepChange = async (targetStep: number) => {
    // 如果从第一步（项目配置）往后跳，需要验证
    if (state.currentStep === 0 && targetStep > 0) {
      if (!validateProjectConfig()) {
        return; // 验证失败，不跳转
      }
    }
    
    // 切换步骤
    updateStep(targetStep);
    
    // 同步到数据库
    if (state.projectId) {
      try {
        await projectApi.updateProgress(state.projectId, {
          current_step: targetStep,
        });
      } catch (error) {
        console.error('更新 current_step 失败:', error);
      }
    }
  };
  
  // 判断步骤4是否完成：所有章节都已生成内容
  const isStep4Completed = () => {
    if (!state.outlineData || !state.outlineData.outline) return false;
    
    // 从 localStorage 加载所有章节内容
    const contentById = draftStorage.loadContentById();
    
    // 递归检查所有叶子节点是否都有内容
    const checkAllLeafContent = (items: any[]): boolean => {
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          // 有子节点，递归检查
          if (!checkAllLeafContent(item.children)) {
            return false;
          }
        } else {
          // 叶子节点，从 contentById 读取内容
          const cachedContent = contentById[item.id];
          if (!cachedContent || cachedContent.trim() === '') {
            return false;
          }
        }
      }
      return true;
    };
    
    return checkAllLeafContent(state.outlineData.outline);
  };
  
  // 判断当前步骤是否已完成（悬浮球是否可点击）
  const isCurrentStepCompleted = () => {
    // 总是检查当前步骤的实际完成状态，不看 maxReachedStep
    switch (state.currentStep) {
      case 0:
        // 项目设置：配置完成即可
        return !!state.projectConfig.company;
      case 1:
        // 招标解析：标记为已完成
        return state.stepsCompleted.analysis;
      case 2:
        // 分项报价：有数据即可（可以后续修改）
        return state.quotationData !== null && state.quotationData.items.length > 0;
      case 3:
        // 大纲编辑：标记为已完成
        return state.stepsCompleted.outline;
      case 4:
        // 内容编辑：检查所有章节是否都已生成
        return isStep4Completed();
      default:
        return false;
    }
  };
  
  // 判断每个步骤是否已完成（有数据）
  const getStepsCompletedArray = () => {
    // 检查分项报价是否完整
    const isQuotationComplete = () => {
      if (!state.quotationData || state.quotationData.items.length === 0) {
        return false;
      }
      
      // 检查是否有最高限价
      if (!state.quotationData.maxTotalPrice || state.quotationData.maxTotalPrice <= 0) {
        return false;
      }
      
      // 检查所有分项的单位、数量、单价都不为空
      const allItemsValid = state.quotationData.items.every(item => {
        return item.unit && 
               item.unit.trim() !== '' && 
               item.quantity > 0 && 
               item.unitPrice !== null && 
               item.unitPrice > 0;
      });
      
      if (!allItemsValid) {
        return false;
      }
      
      // 计算报价总价
      const totalPrice = state.quotationData.items.reduce((sum, item) => {
        return sum + (item.quantity * (item.unitPrice || 0));
      }, 0);
      
      // 检查报价不能超过最高限价
      if (totalPrice > state.quotationData.maxTotalPrice) {
        return false;
      }
      
      return true;
    };
    
    return [
      // 步骤1：项目配置（检查投标主体、项目类型、模型选择、招标文件）
      !!state.projectConfig.company &&
      !!state.projectConfig.projectType &&
      !!state.projectConfig.selectedModel &&
      !!state.fileContent,
      // 步骤2：招标解析（需要5个Tab都有内容）
      !!state.projectOverview && 
      !!state.techRequirements && 
      !!state.outlineRequirements && 
      !!state.disqualification && 
      !!state.quotation,
      // 步骤3：分项报价（检查最高限价、单位、数量、单价和总价不超限）
      isQuotationComplete(),
      // 步骤4：大纲编辑
      state.stepsCompleted.outline,
      // 步骤5：内容编辑（所有章节都已生成内容）
      isStep4Completed(),
    ];
  };
  
  // 点击悬浮球进入下一步（重置后续步骤数据和maxStep）
  const handleNextStep = async () => {
    const nextStepValue = state.currentStep + 1;
    
    // 如果在最后一步，点击"完成制作"按钮
    if (state.currentStep === steps.length - 1) {
      showConfirmDialog(
        '确认完成此标书？',
        '投标文件信息将存入项目管理',
        async () => {
          setLoading({ show: true, message: '正在完成标书...' });
          try {
            // 更新项目状态为已完成
            if (state.projectId) {
              await projectApi.updateProject(state.projectId, { status: 'completed' });
            }
            
            // 清空所有缓存
            resetProject();
            
            showToast('success', '标书制作完成！', ['已成功完成标书制作']);
            setTimeout(() => {
              handleViewChange('project-management');
            }, 1500);
          } catch (error) {
            console.error('完成标书失败:', error);
            showToast('error', '操作失败', ['请稍后重试']);
          } finally {
            setLoading({ show: false, message: '' });
          }
        }
      );
      return;
    }
    
    // 步骤0→1：创建项目并保存到数据库
    if (state.currentStep === 0) {
      // 先验证项目配置
      if (!validateProjectConfig()) {
        return; // 验证失败，不继续
      }
      
      setLoading({ show: true, message: '正在保存项目配置...' });
      try {
        // 如果还没有 projectId，创建新项目
        if (!state.projectId) {
          const projectData = {
            name: state.projectConfig.projectName,
            company_credit_code: state.projectConfig.company!.id,  // 使用统一社会信用代码
            project_type: state.projectConfig.projectType,
            word_count: state.projectConfig.wordCount,
            project_price: state.projectConfig.projectPrice,
            creator: '当前用户', // TODO: 从用户信息获取
            file_content: state.fileContent || '', // 招标文件内容
            ai_model_name: state.projectConfig.aiModelName,
            selected_model: state.projectConfig.selectedModel,
            group_id: state.projectConfig.groupId || undefined,
          };
          
          const response = await projectApi.createProject(projectData);
          const projectId = response.data.id;
          
          // 更新进度
          await projectApi.updateProgress(projectId, {
            current_step: 1,
            max_reached_step: 1,
          });
          
          // 保存projectId到状态
          setProjectId(projectId);
          
          // 导航到项目URL，使后续步骤可以获取projectId
          navigate(`/project/${projectId}`);
          
          showToast('success', '项目配置已保存');
        } else {
          // 如果已有 projectId，更新项目
          await projectApi.updateProject(state.projectId, {
            name: state.projectConfig.projectName,
            alias: state.projectConfig.alias || null,
            company_credit_code: state.projectConfig.company!.id,  // 使用统一社会信用代码
            project_type: state.projectConfig.projectType,
            word_count: state.projectConfig.wordCount,
            project_price: state.projectConfig.projectPrice,
            ai_model_name: state.projectConfig.aiModelName,
            selected_model: state.projectConfig.selectedModel,
            group_id: state.projectConfig.groupId || null,
          });
          
          showToast('success', '项目配置已更新');
        }
        
        // 关闭加载状态
        setLoading({ show: false, message: '' });
      } catch (error) {
        console.error('保存项目失败:', error);
        showToast('error', '保存失败', ['请检查网络连接后重试']);
        setLoading({ show: false, message: '' });
        return; // 保存失败，不继续跳转
      }
    }
    
    // 步骤2→3：从分项报价进入大纲编辑，把报价总价更新到项目配置
    if (state.currentStep === 2 && state.quotationData) {
      setLoading({ show: true, message: '正在更新报价信息...' });
      try {
        // 计算报价总价
        const totalPrice = state.quotationData.items.reduce((sum, item) => {
          return sum + (item.quantity * (item.unitPrice || 0));
        }, 0);
        
        // 更新项目配置中的项目价格
        const updatedConfig = {
          ...state.projectConfig,
          projectPrice: totalPrice,
        };
        updateProjectConfig(updatedConfig);
        
        // 同步更新到数据库
        if (state.projectId) {
          await projectApi.updateProject(state.projectId, {
            project_price: totalPrice,
          });
          console.log(`✅ 项目价格已更新: ${totalPrice}`);
        }
      } catch (error) {
        console.error('更新项目价格失败:', error);
      } finally {
        setLoading({ show: false, message: '' });
      }
    }
    
    // 步骤1→2：从招标解析进入分项报价
    if (state.currentStep === 1) {
      setLoading({ show: true, message: '正在加载分项报价...' });
      // 给一个小延迟让用户感知到加载
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoading({ show: false, message: '' });
    }
    
    // 步骤3→4：从大纲编辑进入内容编辑
    if (state.currentStep === 3) {
      // 进入文件生成前校验别名
      if (!state.projectConfig.alias?.trim()) {
        showToast('error', '请先完善项目信息', ['项目别名不能为空，请到「项目配置」页填写']);
        return;
      }
      setLoading({ show: true, message: '正在加载章节内容...' });
      // 给一个小延迟让用户感知到加载
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoading({ show: false, message: '' });
    }
    
    // 不再重置后续步骤数据，允许前后自由切换
    // resetSubsequentSteps(nextStepValue); // 已移除
    
    // 直接切换到下一步（不需要增量加载，由步骤页面自己加载）
    updateStep(nextStepValue);
    
    console.log('🔍 准备更新进度:', {
      projectId: state.projectId,
      currentStep: state.currentStep,
      nextStepValue,
      maxReachedStep: state.maxReachedStep
    });
    
    // 更新数据库的进度
    if (state.projectId) {
      try {
        // max_reached_step 应该是当前最大值和新步骤值的较大者
        const newMaxReached = Math.max(state.maxReachedStep, nextStepValue);
        
        await projectApi.updateProgress(state.projectId, {
          current_step: nextStepValue,
          max_reached_step: newMaxReached,
        });
        console.log(`✅ 项目进度已更新: 步骤 ${nextStepValue}/4 (最高: ${newMaxReached})`);
      } catch (error) {
        console.error('更新项目进度失败:', error);
      }
    } else {
      console.warn('⚠️ 没有 projectId，无法更新进度');
    }
  };

  // 返回上一步
  const handlePrevStep = async () => {
    if (state.currentStep > 0) {
      const prevStepValue = state.currentStep - 1;
      updateStep(prevStepValue);
      
      // 更新数据库的 current_step（max_reached_step 不变）
      if (state.projectId) {
        try {
          await projectApi.updateProgress(state.projectId, {
            current_step: prevStepValue,
          });
          console.log(`✅ 返回到步骤 ${prevStepValue}`);
        } catch (error) {
          console.error('更新项目进度失败:', error);
        }
      }
    }
  };

  const renderCurrentPage = () => {
    switch (state.currentStep) {
      case 0:
        // 如果没有 projectId，显示快速开始页面
        if (!state.projectId) {
          return (
            <QuickStart
              onProjectCreated={(projectId, fileContent, projectName) => {
                // 更新状态
                setProjectId(projectId);
                updateFileContent(fileContent);
                if (projectName) {
                  updateProjectConfig({ ...state.projectConfig, projectName });
                }
                // 导航到项目URL
                navigate(`/project/${projectId}`);
              }}
            />
          );
        }
        return (
          <ProjectSetup
            projectConfig={state.projectConfig}
            fileContent={state.fileContent}
            projectId={state.projectId}
            onProjectConfigChange={updateProjectConfig}
            onFileUpload={updateFileContent}
            onNext={() => {
              handleNextStep();
            }}
          />
        );
      case 1:
        return (
          <DocumentAnalysis
            fileContent={state.fileContent}
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            outlineRequirements={state.outlineRequirements}
            disqualification={state.disqualification}
            quotation={state.quotation}
            templates={state.templates}
            selectedModel={state.projectConfig.selectedModel}
            projectId={state.projectId}
            tokenBalance={balance?.remaining ?? null}
            // 单个Tab解析完成，立即保存到数据库
            onTabAnalysisComplete={async (tabType, content) => {
              if (state.projectId) {
                try {
                  await projectApi.saveAnalysis({
                    project_id: state.projectId,
                    analysis_type: tabType,
                    content: content,
                  });
                  
                  // 根据不同Tab类型更新对应的本地状态
                  if (tabType === 'overview') {
                    updateAnalysisResults(content, state.techRequirements, state.outlineRequirements, state.disqualification);
                  } else if (tabType === 'requirements') {
                    updateAnalysisResults(state.projectOverview, content, state.outlineRequirements, state.disqualification);
                  } else if (tabType === 'outline') {
                    updateAnalysisResults(state.projectOverview, state.techRequirements, content, state.disqualification);
                  } else if (tabType === 'disqualification') {
                    updateAnalysisResults(state.projectOverview, state.techRequirements, state.outlineRequirements, content);
                  } else if (tabType === 'quotation') {
                    updateQuotation(content);
                  } else if (tabType === 'templates') {
                    updateTemplates(content);
                  }
                } catch (error) {
                  console.error(`保存 ${tabType} 失败:`, error);
                }
              }
            }}
            // 项目名称提取完成，更新本地状态
            onProjectNameExtracted={(name) => {
              updateProjectConfig({
                ...state.projectConfig,
                projectName: name
              });
            }}
            // 所有Tab解析完成，更新进度
            onAnalysisComplete={async (overview, requirements, outlineReqs, disqualification) => {
              // 更新本地状态
              updateAnalysisResults(overview, requirements, outlineReqs, disqualification);
              markAnalysisCompleted();
              
              // 更新进度
              if (state.projectId) {
                try {
                  await projectApi.updateProgress(state.projectId, {
                    analysis_completed: true,
                  });
                } catch (error) {
                  console.error('更新进度失败:', error);
                }
              }
            }}
            onNext={() => {
              handleNextStep();
            }}
            onPrev={() => {
              handlePrevStep();
            }}
          />
        );
      case 2:
        return (
          <QuotationEdit
            initialData={state.quotationData}
            onSave={(data: QuotationData) => {
              // 更新本地状态和 localStorage
              updateQuotationData(data);
              updateStep(2); // 保持在当前步骤
              markQuotationCompleted();
              
              // 注：分项报价数据已通过 quotationApi 保存到专用表，无需再保存到 analysis 表
            }}
            onNext={() => {
              handleNextStep();
              // 滚动到顶部
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onPrev={() => {
              handlePrevStep();
            }}
          />
        );
      case 3:
        return (
          <OutlineEdit
            projectId={state.projectId}  // 添加projectId
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            outlineRequirements={state.outlineRequirements}
            projectName={state.projectConfig.projectName}
            selectedModel={state.projectConfig.selectedModel}
            projectWordCount={state.projectConfig.wordCount}  // 传递项目总字数
            outlineData={state.outlineData}
            tokenBalance={balance?.remaining ?? null}
            onOutlineGenerated={async (outline, skipSave = false) => {
              // 更新本地状态
              updateOutline(outline);
              markOutlineCompleted();
              
              // 如果 skipSave=true（字数分配时），后端已保存，无需再次保存和更新进度
              if (skipSave) {
                return;
              }
              
              // 其他情况（手动编辑目录等），需要保存到数据库
              if (state.projectId) {
                await saveOutlineImmediately(state.projectId, outline);
                
                // 更新进度
                try {
                  await projectApi.updateProgress(state.projectId, {
                    outline_completed: true,
                  });
                } catch (error) {
                  console.error('更新进度失败:', error);
                }
              }
            }}
            onNext={() => {
              handleNextStep();
            }}
            onPrev={() => {
              handlePrevStep();
            }}
            onStepChange={(step) => {
              // 重新生成目录时，重置 maxReachedStep 到当前步骤（防止文件生成步骤仍可点击）
              updateStepAndResetMax(step);
            }}
          />
        );
      case 4:
        return (
          <ContentEdit
            outlineData={state.outlineData}
            selectedChapter={state.selectedChapter}
            onChapterSelect={updateSelectedChapter}
            isStepSidebarCollapsed={isStepSidebarCollapsed}
            projectId={state.projectId}
            projectName={state.projectConfig.projectName}
            selectedModel={state.projectConfig.selectedModel}
            tokenBalance={balance?.remaining ?? null}
            onChapterSave={(chapterId, content) => {
              // 防抖保存章节到数据库
              if (state.projectId) {
                debouncedSaveChapter(state.projectId, chapterId, content);
              }
            }}
            onRefreshOutline={(projectId) => {
              // 刷新目录数据
              refreshOutlineData(projectId);
            }}
            onProjectComplete={async () => {
              // 完成项目
              if (state.projectId) {
                try {
                  await projectApi.updateProject(state.projectId, { status: 'completed' });
                  resetProject();
                  showToast('success', '标书制作完成！');
                  setTimeout(() => {
                    handleViewChange('project-management');
                  }, 1500);
                } catch (error) {
                  console.error('完成标书失败:', error);
                  showToast('error', '操作失败', ['请稍后重试']);
                }
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  // 退出编辑的确认逻辑
  const handleExitEdit = async () => {
    // 快速页（没有 projectId）直接退出，不提示保存
    if (!state.projectId) {
      handleViewChange('project-management');
      return;
    }

    // 根据当前步骤保存数据
    const saveCurrentStepData = async () => {
      if (!state.projectId) return false;
      
      try {
        // 步骤0：项目配置页 - 保存项目配置
        if (state.currentStep === 0) {
          // 验证必填项
          if (!state.projectConfig.company || !state.projectConfig.projectType) {
            showToast('warning', '项目配置不完整', ['请至少填写投标主体和项目类型']);
            return false;
          }
          
          setLoading({ show: true, message: '正在保存项目配置...' });
          await projectApi.updateProject(state.projectId, {
            name: state.projectConfig.projectName,
            alias: state.projectConfig.alias || null,
            company_credit_code: state.projectConfig.company!.id,  // 使用统一社会信用代码
            project_type: state.projectConfig.projectType,
            word_count: state.projectConfig.wordCount,
            project_price: state.projectConfig.projectPrice,
            ai_model_name: state.projectConfig.aiModelName,
            selected_model: state.projectConfig.selectedModel,
            group_id: state.projectConfig.groupId || null,
          });
          showToast('success', '项目配置已保存');
        }
        
        // 步骤2：分项报价页 - 数据会自动保存，无需额外处理
        // QuotationEdit 组件内部已实现自动保存逻辑
        
        return true;
      } catch (error) {
        console.error('保存数据失败:', error);
        showToast('error', '保存失败', ['请检查网络连接后重试']);
        return false;
      } finally {
        setLoading({ show: false, message: '' });
      }
    };

    // 退出时保存数据（不进行必填校验）
    const saveOnExit = async (): Promise<boolean> => {
      if (!state.projectId) return true;
      
      try {
        if (state.currentStep === 0) {
          setLoading({ show: true, message: '正在保存...' });
          await projectApi.updateProject(state.projectId, {
            name: state.projectConfig.projectName,
            alias: state.projectConfig.alias || null,
            company_credit_code: state.projectConfig.company?.id || null,
            project_type: state.projectConfig.projectType || null,
            word_count: state.projectConfig.wordCount,
            project_price: state.projectConfig.projectPrice,
            ai_model_name: state.projectConfig.aiModelName,
            selected_model: state.projectConfig.selectedModel,
            group_id: state.projectConfig.groupId || null,
          });
        }
        return true;
      } catch (error) {
        console.error('保存失败:', error);
        return false;
      } finally {
        setLoading({ show: false, message: '' });
      }
    };

    // 显示确认对话框
    showConfirmDialog(
      '确认退出编辑？',
      '退出前将自动保存当前页面数据',
      async () => {
        // 先保存数据（不进行必填校验）
        await saveOnExit();
        // 返回项目管理列表
        handleViewChange('project-management');
      }
    );
  };

  // 删除项目的处理逻辑 - 使用确认对话框
  const handleDeleteProject = () => {
    if (!state.projectId) {
      showToast('error', '删除失败', ['项目ID不存在']);
      return;
    }
    
    showConfirmDialog(
      '确认删除项目？',
      `确定要删除项目「${state.projectConfig.projectName || '未命名项目'}」吗？此操作不可恢复。`,
      async () => {
        const projectId = state.projectId;
        if (!projectId) return;
        
        try {
          setLoading({ show: true, message: '正在删除项目...' });
          await projectApi.deleteProject(projectId);
          
          // 清空当前项目状态
          resetProject();
          
          // 返回快速创建页 - 使用 new-project 视图
          handleViewChange('new-project');
          showToast('success', '项目已删除');
        } catch (error) {
          console.error('删除项目失败:', error);
          showToast('error', '删除失败', ['请稍后重试']);
        } finally {
          setLoading({ show: false, message: '' });
        }
      }
    );
  };

  // 判断是否在快速创建页（没有 projectId 时）
  const isQuickStart = !state.projectId;

  // 处理项目名称更新
  const handleProjectNameUpdate = async (newName: string) => {
    if (!state.projectId) return;
    
    try {
      // 更新到数据库（更新别名alias，不是name）
      await projectApi.updateProject(state.projectId, { alias: newName });
      
      // 更新本地状态
      updateProjectConfig({
        ...state.projectConfig,
        alias: newName
      });
    } catch (error) {
      console.error('更新项目别名失败:', error);
      throw error; // 重新抛出错误，让StepSidebar处理
    }
  };

  
  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex relative">
      {/* Token使用进度条 - 绝对定位悬浮在最顶部，不占用空间 */}
      <div className="group absolute top-0 left-0 right-0 z-50">
        <TokenProgressBar />
      </div>

      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={6000}
      />

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="确定"
        cancelText="取消"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          confirmDialog.onConfirm();
          closeConfirmDialog();
        }}
        onCancel={closeConfirmDialog}
      />

      {/* Token 预警弹窗 */}
      <TokenAlertDialog
        isOpen={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onRecharge={handleRecharge}
        onClose={closeAlert}
        onContinue={handleContinue}
        canContinue={alert.canContinue}
      />

      {/* 根据当前视图显示不同的布局 */}
      {currentView === 'new-project' ? (
        <>
          {/* 左侧步骤导航卡片（替换旧的Sidebar） */}
          <StepSidebar
            steps={steps}
            currentStep={state.currentStep}
            maxReachedStep={state.maxReachedStep} // 新增
            stepsCompleted={getStepsCompletedArray()}
            onStepClick={handleStepChange}
            onExit={handleExitEdit}
            onDeleteProject={handleDeleteProject} // 新增：删除项目
            canDelete={!isQuickStart} // 新增：快速创建页不可删除
            projectName={state.projectConfig.projectName || '新建标书项目'}
            alias={state.projectConfig.alias} // 新增：项目别名（优先显示）
            projectId={state.projectId} // 新增：项目ID
            onProjectNameUpdate={handleProjectNameUpdate} // 新增：项目名称更新回调
            onShowToast={(type, title) => showToast(type, title)} // 新增：Toast通知回调
            onCollapseChange={setIsStepSidebarCollapsed}
            stepTimestamps={[]} // TODO: 从数据库加载时间戳
          />

          {/* 主内容区域 */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {state.currentStep === 4 || !state.projectId ? (
              // 步骤5：内容编辑页面 或 快速启动页：占据全部空间，无padding
              renderCurrentPage()
            ) : (
              // 其他步骤：带padding和滚动
              <div id="app-main-scroll" className="flex-1 p-6 overflow-y-auto">
                {renderCurrentPage()}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* 左侧导航栏悬浮卡片容器 */}
          <div className="py-3 pl-3 pr-3 flex-shrink-0">
            <div className="h-full rounded-2xl overflow-hidden shadow-xl" style={{ boxShadow: '0 8px 32px rgba(99,102,241,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}>
              <Sidebar
                activeView={currentView}
                onViewChange={handleViewChange}
                currentUser={{
                  id: String(auth.user?.id || '0'),
                  username: auth.user?.username || '',
                  name: auth.user?.display_name || auth.user?.username || '用户',
                  role: (auth.user?.role as 'super' | 'admin' | 'user') || 'user'
                }}
                onLogout={handleLogout}
              />
            </div>
          </div>

          {/* 主内容区域（管理页面） */}
          <div className="flex-1 flex flex-col min-w-0 py-3 pr-3">
            <div className="h-full rounded-2xl overflow-hidden shadow-xl bg-white flex flex-col" style={{ boxShadow: '0 8px 32px rgba(99,102,241,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}>
              {currentView === 'project-management' ? (
              <ProjectManagement onEditProject={handleEditProject} />
            ) : currentView === 'template-management' ? (
              <TemplateManagement />
            ) : currentView === 'company-management' ? (
              <CompanyManagement />
            ) : currentView === 'company-edit' ? (
              <CompanyEdit />
            ) : currentView === 'resource-management' ? (
              <ResourceManagement />
            ) : currentView === 'token-management' ? (
              <TokenManagement currentUser={{ id: auth.user?.id || 0, name: auth.user?.display_name || '', role: auth.user?.role || '' }} />
            ) : currentView === 'user-management' ? (
              <UserManagement currentUser={{ id: auth.user?.id || 0, name: auth.user?.display_name || '', role: auth.user?.role || '' }} />
            ) : currentView === 'project-group-management' ? (
              <ProjectGroupManagement />
            ) : null}
            </div>
          </div>
        </>
      )}

      {/* 全屏加载遮罩 */}
      <LoadingOverlay show={loading.show} message={loading.message} />

      {/* 全局登录弹框 - 用于鉴权失败时弹出 */}
      <LoginDialog
        isOpen={auth.showLoginDialog}
        onClose={() => auth.setShowLoginDialog(false)}
        onSuccess={auth.handleLoginSuccess}
      />

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={companyConfirmDialog.show}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setCompanyConfirmDialog({ show: false, hasCompany: null });
          navigate('/companies/new');
        }}
        onCancel={() => {
          setCompanyConfirmDialog({ show: false, hasCompany: null });
          doCreateProject(); // 点击"去创建标书"，执行创建标书流程
        }}
        onClose={() => {
          setCompanyConfirmDialog({ show: false, hasCompany: null }); // 点击外部仅关闭弹框，不做任何操作
        }}
      />
    </div>
  );
}

export default App;
